import { Component } from "react";
import type { FC, PropsWithChildren, ReactNode } from "react";
import { Redirect } from "expo-router";
import { TRPCClientError } from "@trpc/client";

import { api } from "~/utils/api";
import { MeContext } from "~/hooks/use-me";

export const OnlyAuthorized: FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <UnauthorizedErrorBoundary>
      <MeProvider>{children}</MeProvider>
    </UnauthorizedErrorBoundary>
  );
};

const MeProvider: FC<PropsWithChildren> = ({ children }) => {
  const [person] = api.context.person.useSuspenseQuery(undefined, {
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  });

  if (!person) return <Redirect href={"/auth"} />;

  return <MeContext.Provider value={person}>{children}</MeContext.Provider>;
};

interface UnauthorizedErrorBoundaryState {
  unauthorized: boolean;
}

class UnauthorizedErrorBoundary extends Component<
  PropsWithChildren,
  UnauthorizedErrorBoundaryState
> {
  public state: UnauthorizedErrorBoundaryState = { unauthorized: false };

  public static getDerivedStateFromError(
    error: unknown,
  ): UnauthorizedErrorBoundaryState {
    if (
      error instanceof TRPCClientError &&
      error.data?.code === "UNAUTHORIZED"
    ) {
      return { unauthorized: true };
    }

    throw error;
  }

  public render() {
    if (this.state.unauthorized || !this.props.children) {
      return <Redirect href={"/auth"} />;
    }

    return this.props.children;
  }
}
